import React, { useState } from 'react';
import { Box, Typography, Grow, CircularProgress, Skeleton, Switch, FormControlLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const BRAND_TEAL = '#2ec4b6';

const ROUND_WORDS = ['One', 'Two', 'Three', 'Four', 'Five'];

const SectionLabel = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '0.75rem',
  letterSpacing: '0.32em',
  color: '#888',
  textTransform: 'uppercase',
  fontWeight: 700,
});

const RoundHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  marginBottom: 14,
});

const ShowToggle = styled(FormControlLabel)({
  margin: 0,
  gap: 6,
  '& .MuiFormControlLabel-label': {
    fontFamily: 'Helvetica Inserat, Impact, sans-serif',
    fontSize: '0.7rem',
    letterSpacing: '0.18em',
    color: '#1a7a72',
    textTransform: 'uppercase',
    fontWeight: 700,
  },
  '& .MuiSwitch-root': {
    padding: 6,
  },
  '& .Mui-checked + .MuiSwitch-track': {
    backgroundColor: `${BRAND_TEAL} !important`,
    opacity: '0.55 !important',
  },
  '& .Mui-checked .MuiSwitch-thumb': {
    color: BRAND_TEAL,
  },
});

const CardGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 10,
  [theme.breakpoints.down('md')]: { gridTemplateColumns: 'repeat(2, 1fr)' },
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
}));

const Card = styled(Box)({
  background: 'rgba(255,255,255,0.75)',
  borderLeft: `4px solid ${BRAND_TEAL}`,
  borderRadius: 8,
  padding: '12px 14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 52,
  boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
});

const CardLabel = styled(Box)({
  position: 'relative',
  minHeight: '1.1em',
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '0.9rem',
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  fontWeight: 700,
  lineHeight: 1.1,
  flex: 1,
});

const Anon = styled('span', { shouldForwardProp: (p) => p !== 'visible' })(({ visible }) => ({
  display: 'block',
  color: '#0f4a44',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(4px)',
  transition: 'opacity 0.4s ease, transform 0.4s ease',
  position: 'absolute',
  top: 0, left: 0, right: 0,
}));

const Real = styled('span', { shouldForwardProp: (p) => p !== 'visible' })(({ visible }) => ({
  display: 'block',
  color: '#1a7a72',
  letterSpacing: '0.08em',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(-4px)',
  transition: 'opacity 0.4s ease, transform 0.4s ease',
  position: 'absolute',
  top: 0, left: 0, right: 0,
}));

const StatusIcon = ({ status }) => {
  if (status === 'complete') {
    return <CheckCircleIcon sx={{ color: BRAND_TEAL, fontSize: 22 }} />;
  }
  if (status === 'in_progress' || status === 'generating' || status === 'validating') {
    return <CircularProgress size={18} thickness={4} sx={{ color: BRAND_TEAL }} />;
  }
  return <RadioButtonUncheckedIcon sx={{ color: 'rgba(0,0,0,0.2)', fontSize: 22 }} />;
};

const IN_PROGRESS_STATUSES = new Set(['in_progress', 'generating', 'validating']);

// Categories run in parallel, so #3 can finish before #1. The progress map is
// keyed by category name, so we look up status by name rather than counting
// completions positionally (which would show whichever cards happen to come
// first as complete, regardless of which actually finished).
const getStatusForCard = (globalIndex, categoryProgress, categoryNames, currentPhase) => {
  if (['initializing', 'categories', 'ideas'].includes(currentPhase)) return 'pending';
  if (['finalJeopardy', 'assembly', 'complete'].includes(currentPhase)) return 'complete';

  const name = categoryNames[globalIndex];
  if (name) {
    // We know which category this card represents — look up status by name.
    // If the name isn't in the map yet, the category hasn't started: pending.
    const entry = (categoryProgress || {})[name];
    if (entry?.status === 'complete') return 'complete';
    if (entry && IN_PROGRESS_STATUSES.has(entry.status)) return 'in_progress';
    return 'pending';
  }

  // Names not yet loaded — fall back to positional counting so the grid
  // isn't blank during the brief window before categoriesList lands.
  const entries = Object.values(categoryProgress || {});
  const completedCount = entries.filter((c) => c.status === 'complete').length;
  const inProgressCount = entries.filter((c) => IN_PROGRESS_STATUSES.has(c.status)).length;
  if (globalIndex < completedCount) return 'complete';
  if (globalIndex < completedCount + inProgressCount) return 'in_progress';
  return 'pending';
};

const CategoryProgress = ({ categoryCount, roundCount = 1, categoryProgress = {}, categoryNames = [], currentPhase }) => {
  const [showNames, setShowNames] = useState({});

  if (categoryCount === 0) {
    return (
      <Box sx={{ mb: 3 }}>
        <SectionLabel sx={{ textAlign: 'center', mb: 1.5, display: 'block' }}>Categories</SectionLabel>
        <CardGrid>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={52} data-skeleton sx={{ borderRadius: 1, bgcolor: 'rgba(0,0,0,0.04)' }} />
          ))}
        </CardGrid>
      </Box>
    );
  }

  const perRound = roundCount > 0 ? Math.floor(categoryCount / roundCount) : categoryCount;
  const rounds = Array.from({ length: roundCount }).map((_, roundIdx) => ({
    label: roundCount === 1 ? 'Categories' : `Categories — Round ${ROUND_WORDS[roundIdx] || roundIdx + 1}`,
    startIdx: roundIdx * perRound,
    endIdx: Math.min((roundIdx + 1) * perRound, categoryCount),
  }));

  return (
    <Box sx={{ mb: 3 }}>
      {rounds.map((round, roundIdx) => {
        const cards = [];
        for (let i = round.startIdx; i < round.endIdx; i++) {
          const status = getStatusForCard(i, categoryProgress, categoryNames, currentPhase);
          const localIdx = i - round.startIdx;
          const isOn = !!showNames[roundIdx];
          const hasName = !!categoryNames[i];
          const reveal = isOn && hasName;
          cards.push(
            <Grow in key={i} timeout={400 + localIdx * 120}>
              <Card data-cat-card={i} data-card-status={status} data-show-name={reveal}>
                <CardLabel>
                  <Anon visible={!reveal}>Category {localIdx + 1}</Anon>
                  <Real visible={reveal}>{categoryNames[i] || ''}</Real>
                </CardLabel>
                <StatusIcon status={status} />
              </Card>
            </Grow>
          );
        }
        return (
          <Box key={roundIdx} sx={{ mt: roundIdx === 0 ? 0 : 3 }}>
            <RoundHeader>
              <SectionLabel>{round.label}</SectionLabel>
              {categoryNames.length > 0 && (
                <ShowToggle
                  control={
                    <Switch
                      size="small"
                      checked={!!showNames[roundIdx]}
                      onChange={() => setShowNames((s) => ({ ...s, [roundIdx]: !s[roundIdx] }))}
                    />
                  }
                  label="Show Categories"
                  labelPlacement="start"
                />
              )}
            </RoundHeader>
            <CardGrid>{cards}</CardGrid>
          </Box>
        );
      })}
    </Box>
  );
};

export default CategoryProgress;
